/**
 * Centralized medical specializations configuration
 * This file contains all medical specializations used across the call-center app
 */

export interface Specialization {
  key: string;
  value: string;
  translations: {
    en: string;
    'zh-HK': string;
    'zh-CN': string;
  };
}

export const SPECIALIZATIONS: Specialization[] = [
  {
    key: 'general-practice',
    value: 'General Practice',
    translations: {
      en: 'General Practice',
      'zh-HK': '普通科',
      'zh-CN': '普通科'
    }
  },
  {
    key: 'general-medicine',
    value: 'General Medicine',
    translations: {
      en: 'General Medicine',
      'zh-HK': '內科',
      'zh-CN': '内科'
    }
  },
  {
    key: 'cardiology',
    value: 'Cardiology',
    translations: {
      en: 'Cardiology',
      'zh-HK': '心臟科',
      'zh-CN': '心脏科'
    }
  },
  {
    key: 'dermatology',
    value: 'Dermatology',
    translations: {
      en: 'Dermatology',
      'zh-HK': '皮膚科',
      'zh-CN': '皮肤科'
    }
  },
  {
    key: 'pediatrics',
    value: 'Pediatrics',
    translations: {
      en: 'Pediatrics',
      'zh-HK': '兒科',
      'zh-CN': '儿科'
    }
  },
  {
    key: 'orthopedics',
    value: 'Orthopedics',
    translations: {
      en: 'Orthopedics',
      'zh-HK': '骨科',
      'zh-CN': '骨科'
    }
  },
  {
    key: 'neurology',
    value: 'Neurology',
    translations: {
      en: 'Neurology',
      'zh-HK': '神經科',
      'zh-CN': '神经科'
    }
  },
  {
    key: 'oncology',
    value: 'Oncology',
    translations: {
      en: 'Oncology',
      'zh-HK': '腫瘤科',
      'zh-CN': '肿瘤科'
    }
  },
  {
    key: 'dentistry',
    value: 'Dentistry',
    translations: {
      en: 'Dentistry',
      'zh-HK': '牙科',
      'zh-CN': '牙科'
    }
  }
];

/**
 * Get specializations formatted for use in forms and dropdowns
 * @param currentLanguage - Current language code
 * @returns Array of specializations with key and translated value
 */
export const getSpecializations = (currentLanguage: string = 'en'): Array<{ key: string; value: string }> => {
  return SPECIALIZATIONS.map(spec => ({
    key: spec.key,
    value: spec.translations[currentLanguage as keyof typeof spec.translations] || spec.value
  }));
};

/**
 * Get specialization label by key for a specific language
 * @param key - Specialization key
 * @param currentLanguage - Current language code
 * @returns Translated specialization label
 */
export const getSpecializationLabel = (key: string, currentLanguage: string = 'en'): string => {
  const spec = SPECIALIZATIONS.find(s => s.key === key);
  if (!spec) return key;
  return spec.translations[currentLanguage as keyof typeof spec.translations] || spec.value;
};

/**
 * Create a mapping from localized labels to raw IDs for filtering
 * @param currentLanguage - Current language code
 * @returns Object mapping localized labels to raw IDs
 */
export const getSpecializationIdMap = (currentLanguage: string = 'en'): { [key: string]: string } => {
  const idMap: { [key: string]: string } = {};
  
  SPECIALIZATIONS.forEach(spec => {
    // Add English mapping
    idMap[spec.value] = spec.key;
    
    // Add current language mapping
    const translatedValue = spec.translations[currentLanguage as keyof typeof spec.translations];
    if (translatedValue && translatedValue !== spec.value) {
      idMap[translatedValue] = spec.key;
    }
    
    // Add all language mappings for comprehensive filtering
    Object.values(spec.translations).forEach(translation => {
      if (translation !== spec.value) {
        idMap[translation] = spec.key;
      }
    });
  });
  
  return idMap;
};

/**
 * Get specialization options for dropdowns with proper value/label mapping
 * @param currentLanguage - Current language code
 * @returns Array of options with label, value, and key
 */
export const getSpecializationOptions = (currentLanguage: string = 'en'): Array<{ label: string; value: string; key: string }> => {
  return SPECIALIZATIONS.map(spec => {
    const translatedValue = spec.translations[currentLanguage as keyof typeof spec.translations] || spec.value;
    return {
      label: translatedValue,
      value: spec.key, // Always use the raw key as value
      key: spec.key
    };
  });
};
